/**
 * [PORTABLE] Reusable single grid cell used by all pillar grids
 * Target path in main project: src/features/onboarding/components/PromptCell.tsx
 */
import { Lock, Check, Play, ChevronsRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export interface PromptCellState {
  url?: string;
  completed: boolean;
  unlocked: boolean;
}

export interface PromptCellProps {
  levelId: string;
  level: number;
  label: string;
  focusLabel: string;
  emoji?: string;
  state: PromptCellState;
  placeholderIconSrc?: string;
  hideCompletedCheck?: boolean;
  onSelect: (levelId: string) => void;
}

export function PromptCell({
  levelId,
  level,
  label,
  focusLabel,
  emoji,
  state,
  placeholderIconSrc,
  hideCompletedCheck,
  onSelect,
}: PromptCellProps) {
  const { t } = useTranslation();

  const isActive = state.unlocked && !state.completed;
  const isCompleted = state.completed;
  const isLocked = !state.unlocked;

  return (
    <div className="relative group w-full">
      
      {/* Aktif Adım Etiketi */}
      {isActive && (
        <div className="absolute -top-4 md:-top-5 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-3 py-1 rounded-full text-[10px] font-bold animate-bounce uppercase tracking-widest z-30 shadow-md whitespace-nowrap">
          {t("onboarding.activeQuest", { defaultValue: "Aktif Adım" })}
        </div>
      )}

      {/* KART GÖVDESİ */}
      <div
        onClick={() => state.unlocked && onSelect(levelId)}
        className={cn(
          "glass-card rounded-2xl overflow-hidden flex transition-all duration-700 w-full relative group-hover:shadow-lg",
          isActive ? "flex-col md:flex-row border border-transparent cursor-pointer" : "flex-col border border-border-primary/50",
          isCompleted && "opacity-80 grayscale-15 hover:grayscale-0 hover:opacity-100 cursor-pointer",
          isLocked && "opacity-50 grayscale blur-[1px] hover:blur-0 cursor-not-allowed border-dashed"
        )}
      >
        {/* Akıcı Parlama (Smooth Pulse) */}
        {isActive && (
          <div 
            className="absolute inset-0 rounded-2xl border-[3px] border-blue-500/40 shadow-[0_0_25px_rgba(59,130,246,0.3)] pointer-events-none z-20" 
            style={{ animation: "pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite" }} 
          />
        )}

        {/* 🚀 SOL/ÜST KISIM: KARE GÖRSEL ALANI */}
        <div className={cn(
          "relative flex items-center justify-center bg-bg-secondary shrink-0 overflow-hidden z-10",
          // Aktifse genişliği sabitleyip (md:w-[260px]) TAM BİR KARE (aspect-square) yapıyoruz
          // İnaktifse sabit 160px yüksekliğinde bir dikdörtgen kalıyor.
          isActive ? "w-full md:w-65 aspect-square" : "w-full h-40"
        )}>
          {state.url ? (
            <>
              <img src={state.url} alt={label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-linear-to-t from-black/50 to-transparent" />
            </>
          ) : placeholderIconSrc ? (
            <img src={placeholderIconSrc} alt="" className="h-10 w-10 mb-2 opacity-50" loading="lazy" />
          ) : (
            <div className={cn("drop-shadow-lg opacity-80", isActive ? "text-6xl md:text-7xl" : "text-5xl")}>{emoji}</div>
          )}

          <div className="absolute top-3 left-3 bg-bg-primary/80 backdrop-blur-md text-text-primary text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest shadow-sm border border-border-primary z-10">
            L{level}
          </div>
        </div>

        {/* SAĞ/ALT KISIM: Metin ve Buton */}
        <div className={cn(
          "flex-1 flex flex-col bg-linear-to-br from-bg-primary to-bg-secondary/30 relative z-10",
          // Aktifse dikeyde ortala (justify-center) ki karenin yanına şık otursun
          isActive ? "p-6 md:p-8 justify-center gap-4" : "p-5 justify-between"
        )}>
          <div>
            <h3 className={cn("font-extrabold mb-1 tracking-tight", isActive ? "text-xl md:text-2xl text-blue-500" : "text-lg text-text-primary")}>
              {label}
            </h3>
            <p className={cn("text-text-secondary font-medium leading-relaxed", isActive ? "text-sm" : "text-xs")}>
              {focusLabel}
            </p>
          </div>

          {/* Aktif: Maceraya Atıl Butonu */}
          {isActive && (
            <Button
              variant="darkPrimary"
              className="w-full py-6 rounded-xl font-bold uppercase tracking-widest shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] relative z-30 mt-2"
              onClick={(e) => {
                e.stopPropagation(); 
                onSelect(levelId);
              }}
            >
              {t("onboarding.ventureForth", { defaultValue: "Maceraya Atıl" })} <ChevronsRight className="h-5 w-5" />
            </Button>
          )}

          {/* Tamamlandı Etiketi */}
          {isCompleted && !hideCompletedCheck && (
            <div className="mt-4 flex items-center gap-1.5 text-green-500 font-bold text-[10px] uppercase tracking-wider">
              <Check className="h-3.5 w-3.5" />
              {t("onboarding.masteryAchieved", { defaultValue: "Tamamlandı" })}
            </div>
          )}

          {/* Kilitli Etiketi */}
          {isLocked && (
            <div className="mt-4 flex items-center gap-1.5 text-text-tertiary font-bold text-[10px] uppercase tracking-wider">
              <Lock className="h-3.5 w-3.5" />
              {t("onboarding.lockedStep", { defaultValue: "Kilitli Adım" })}
            </div>
          )}
        </div>
      </div>

      {/* DIŞ İKONLAR */}
      {isCompleted && !hideCompletedCheck && (
        <div className="hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-green-500 text-white items-center justify-center shadow-lg ring-2 ring-bg-primary z-20 transition-transform group-hover:scale-110">
          <Check className="h-4 w-4" />
        </div>
      )}

      {isActive && (
        <div className="hidden sm:flex absolute -left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-blue-500 text-white items-center justify-center shadow-xl ring-4 ring-blue-500/20 z-20" style={{ animation: "pulse 3s infinite" }}>
          <Play className="h-5 w-5 fill-white ml-0.5" />
        </div>
      )}

      {isLocked && (
        <div className="hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-bg-tertiary text-text-secondary items-center justify-center shadow-md border-2 border-bg-primary z-20">
          <Lock className="h-3.5 w-3.5" />
        </div>
      )}

    </div>
  );
}